// Which part of an element belongs to the LANGUAGE it is written in, and which
// part belongs to the translation group.
//
// A translation group has one structure and many contents: adding, removing,
// reordering or re-settling an element on any page of a group applies to every
// language in it, while the prose and the values filled into it stay per-page.
// This file is the line between the two, and it is imported by both the model
// that enforces it (api/code/src/model/page) and the screens that label it
// (app/code/src/routes/.../page/edit), so the sentence on screen cannot drift
// from the rule the server applies.
//
// THE SPLIT IS THE ONE THIS SERVICE ALREADY MAKES EVERYWHERE ELSE: markup and
// declarations are structure, values are data. A section's `html` is markup, so
// translating it means parameterising it first and putting the string in
// `values`; the site translation catalog then renders one section row in every
// language (docs/pagews.md § The translation catalog). A `code` element is an
// anonymous section, so it splits the same way — `html`, `style`, `script` and
// the `types` declaration are the group's, `values` is the language's. A
// `paragraph` is prose and nothing else, so all of it is the language's. And a
// `section` element is a POINTER at a Section row, which every page and every
// language already shares, so it has nothing of its own to translate.
import { types } from "./fields";

// Keyed by the element's `type`, naming properties of its `config`.
//
// A type absent from this map has nothing translatable, which is the safe
// default: an element type added later shares everything until somebody decides
// otherwise, rather than silently diverging per language on its first save.
export const TRANSLATABLE = {
  section: [],
  code: ["values"],
  paragraph: ["content"],
  // The blog elements carry POINTERS — a slug pattern, a section, a
  // collection — and the prose they show belongs to the post, which is not
  // per-page. Nothing on them is a language's own.
  "blog-post": [],
  "blog-list": [],
  "blog-post-collection": [],
};

// Asserted rather than assumed: `types` is the enum /Page/Element resolves its
// `config` branch by, and a type that never reached this map would be shared
// whole — including a prose field — with nothing to say so.
export const translatableKeys = (type) =>
  (Object.prototype.hasOwnProperty.call(TRANSLATABLE, type) && TRANSLATABLE[type]) || [];

// Every element type this service knows, so a test can walk them.
export { types };

const pick = (source, keys) =>
  keys.reduce((res, key) => {
    if (source && source[key] !== undefined) {
      res[key] = source[key];
    }

    return res;
  }, {});

const omit = (source, keys) =>
  Object.keys(source || {}).reduce((res, key) => {
    if (!keys.includes(key)) {
      res[key] = source[key];
    }

    return res;
  }, {});

// The element's config, narrowed to what this language holds.
export const translatableOf = (element) =>
  pick((element && element.config) || {}, translatableKeys(element && element.type));

// The whole element, minus that — what every language in the group holds
// identically. `translationPending` is not here: it is a statement ABOUT one
// language's copy, so it is neither shared nor content, and
// @common/schema/page/translation decides it per row.
//
// THE DEFAULTS ARE `normalizeElements`' OWN (api/code/src/model/page), and they
// have to be, for two reasons at once. This output is written STRAIGHT to a
// sibling row — a propagating write does not go back through the update path —
// so a frame defaulted to "" here would land on a sibling as an element wrapped
// in nothing, which renders as nothing. And it is also the projection compared
// to decide whether the structure MOVED: defaulting differently from the writer
// makes every first save of an older row look like a structural change.
export const sharedOf = (element) => ({
  elementid: element && element.elementid,
  name: (element && element.name) || "",
  type: element && element.type,
  tags: (element && element.tags) || [],
  frame: (element && element.frame) || "<div><%-content%></div>",
  config: omit((element && element.config) || {}, translatableKeys(element && element.type)),
});

// Whether a value is one a translator would act on.
//
// The empty cases are not all "" — a `code` element's untouched `values` is
// `{}`, and a `paragraph` that was added and never typed into holds the JSON
// string `{"ops":[]}`, which is the empty document Quill's own `setContents`
// accepts and what /Page/Element/Type/Paragraph declares as its default.
const isEmptyValue = (value) => {
  if (value === undefined || value === null || value === "") {
    return true;
  }

  // RECURSIVE, because the value that matters most here is an object: a `code`
  // element's `values` is one entry per declared param, so a page whose params
  // are declared and blank holds `{ heading: "", cta: "" }` rather than `{}`.
  // Counting that as prose would leave a page reading "0 of 3 translated" with
  // nothing on it for a translator to write.
  if (Array.isArray(value)) {
    return value.every(isEmptyValue);
  }

  if (typeof value === "object") {
    return Object.values(value).every(isEmptyValue);
  }

  if (typeof value === "string") {
    // Parsed rather than compared to the literal: Quill writes no whitespace in
    // it today and that is not a promise anybody made.
    try {
      const parsed = JSON.parse(value);

      return Boolean(parsed) && Array.isArray(parsed.ops) && parsed.ops.length === 0;
    } catch (e) {
      return false;
    }
  }

  return false;
};

// Whether this element holds anything to translate at all.
//
// A `section` element never does, and neither does a `code` element whose
// author has not filled a single value in — so neither is ever counted as
// untranslated, and a page made entirely of section elements reads as fully
// translated rather than as permanently behind.
export const hasTranslatable = (element) => {
  const values = translatableOf(element);

  return Object.keys(values).some((key) => !isEmptyValue(values[key]));
};

// JSON with object keys in a stable order, so two values built by different
// code paths compare as the same string.
//
// Both comparisons in this feature are over documents that reached us as JSON
// on the wire and go back to the table as JSON, so there is nothing in them a
// structural walk would handle that this does not.
export const stable = (value) => {
  if (Array.isArray(value)) {
    return `[${value.map(stable).join(",")}]`;
  }

  if (value && typeof value === "object") {
    return `{${Object.keys(value)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stable(value[key])}`)
      .join(",")}}`;
  }

  return JSON.stringify(value === undefined ? null : value);
};

// Whether two versions of one element say the same thing in this language.
// What clears the pending mark: an author who edited the prose has translated
// it, and nothing else has to be pressed.
export const sameTranslatable = (left, right) =>
  stable(translatableOf(left)) === stable(translatableOf(right));
